import React, { useMemo, useState } from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";

// What the import actually did, one line per row read.
//
// Every message shown here comes from the response. The backend knows whether an order was created,
// updated or refused, and it owns the wording; composing a message here would be a second version
// of the truth that drifts the moment the rules change.

const SUCCEEDED_TAB = "succeeded";
const FAILED_TAB = "failed";

// The sales channels the campaign runs on. The API drops everything else before it answers, so
// this is a guard rather than a filter: if a row on another channel ever reaches the screen it is
// a fault upstream, and showing it would tell an administrator an order counts when it does not.
const CAMPAIGN_SALES_CHANNELS = ["5B", "5C", "5D", "5E", "5F", "5J", "5L", "5S"];

function isCampaignRow(row) {
     return CAMPAIGN_SALES_CHANNELS.includes((row.salesChannel || "").toUpperCase());
}

// The result arrives whole, so the page is taken here. Ten rows a page is what every other table
// in the portal shows.
const INITIAL_PAGE = { skip: 0, take: 10 };

export default function LcvOrderImportResults(props) {
     const { result, onDone } = props;

     const [tab, setTab] = useState(SUCCEEDED_TAB);
     const [page, setPage] = useState(INITIAL_PAGE);

     const rows = useMemo(() => (result?.rows || []).filter(isCampaignRow), [result]);

     const succeededRows = useMemo(() => rows.filter((row) => row.isValid), [rows]);
     const failedRows = useMemo(() => rows.filter((row) => !row.isValid), [rows]);

     const shownRows = tab === SUCCEEDED_TAB ? succeededRows : failedRows;

     const pageRows = shownRows.slice(page.skip, page.skip + page.take);

     return (
          <div>
               <Typography variant="h6" sx={{ mb: 1 }}>
                    {result?.message}
               </Typography>

               <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {result?.succeeded} imported, {result?.failed} failed, out of {result?.total}{" "}
                    rows read. {result?.created} new, {result?.updated} already held,{" "}
                    {result?.eligible} counting towards a game code.
               </Typography>

               <Alert severity="info" sx={{ mb: 2 }}>
                    Campaigns: {result?.campaignSummary}
               </Alert>

               <Tabs
                    value={tab}
                    onChange={(event, chosen) => {
                         setTab(chosen);
                         setPage(INITIAL_PAGE);
                    }}
                    sx={{ mb: 1 }}
               >
                    <Tab value={SUCCEEDED_TAB} label={`Imported (${succeededRows.length})`} />
                    <Tab value={FAILED_TAB} label={`Failed (${failedRows.length})`} />
               </Tabs>

               <Grid
                    data={pageRows}
                    className="sba-area-data-grid"
                    scrollable="none"
                    skip={page.skip}
                    take={page.take}
                    total={shownRows.length}
                    pageable={{ buttonCount: 4, pageSizes: [10, 25, 50] }}
                    onPageChange={(event) => setPage(event.page)}
               >
                    <GridColumn field="rowNumber" title="Row" />
                    <GridColumn field="customerOrderNumber" title="Order" />
                    <GridColumn field="chassisNumber" title="Chassis" />
                    <GridColumn field="registrationNumber" title="Registration" />
                    <GridColumn field="dealerCode" title="Dealer Code" />
                    <GridColumn field="dealerName" title="Dealer" />
                    <GridColumn field="campaignName" title="Campaign" />
                    <GridColumn field="message" title="What happened" />
               </Grid>

               <Box sx={{ mt: 2 }}>
                    <Button type="button" themeColor="primary" onClick={onDone}>
                         Done
                    </Button>
               </Box>
          </div>
     );
}
